import { useEffect, useState } from 'react'
import { getProgram } from '../api/programs'
import { label } from './labels'

export interface ProgramRef {
  code: string
  jurisdiction: string
  /** `"{code} ({jurisdiction label})"` — the one string every caller renders instead of a bare
   * `programId`/"Program {uuid}" placeholder. */
  display: string
}

/**
 * Resolves `Program.code`/`jurisdiction` for a set of `programId`s, client-side — the same
 * batched, distinct-id, resolve-once shape {@link useCaseContexts} (`screens/tasks/`) uses. Exists
 * because `ProgramFilingDetailView` (the Case-workspace tabs' read shape) carries only a bare
 * `programId`, unlike the tenant-wide `ProgramFilingListView` which already denormalizes
 * `programName`/`jurisdiction` server-side (`case_.api.ProgramFilingApi.list`). A failed/missing
 * lookup degrades that one id to absent from the map (never a thrown render error) — callers fall
 * back to a generic `EntityRef` "Program {shortId}" label, never a raw full UUID.
 */
export function useProgramRefs(programIds: (string | null | undefined)[]): Map<string, ProgramRef> {
  const [refs, setRefs] = useState<Map<string, ProgramRef>>(new Map())
  const key = Array.from(new Set(programIds.filter((id): id is string => Boolean(id))))
    .sort()
    .join(',')

  useEffect(() => {
    const distinctIds = key ? key.split(',') : []
    if (distinctIds.length === 0) {
      setRefs(new Map())
      return
    }
    let cancelled = false
    void Promise.all(
      distinctIds.map(async (id): Promise<[string, ProgramRef] | null> => {
        try {
          const program = await getProgram(id)
          const jurisdictionLabel = label('jurisdiction', program.jurisdiction)
          return [id, { code: program.code, jurisdiction: program.jurisdiction, display: `${program.code} (${jurisdictionLabel})` }]
        } catch {
          return null
        }
      }),
    ).then((entries) => {
      if (cancelled) return
      const next = new Map<string, ProgramRef>()
      for (const entry of entries) {
        if (entry) next.set(entry[0], entry[1])
      }
      setRefs(next)
    })
    return () => {
      cancelled = true
    }
    // `key` is the stable, sorted, de-duplicated dependency — see useCaseContexts's own note.
  }, [key])

  return refs
}
